/**
 * Composable for pinch-to-zoom on touch devices
 */

import { ref, onMounted, onUnmounted, type Ref } from 'vue'
import { clampZoom, MIN_ZOOM, MAX_ZOOM } from '../utils/zoom-utils'

export interface UsePinchZoomOptions {
  containerRef: Ref<HTMLElement | null>
  currentZoom: Ref<number>
  onZoomChange: (zoom: number) => void
  minZoom?: number
  maxZoom?: number
}

export function usePinchZoom(options: UsePinchZoomOptions) {
  const {
    containerRef,
    currentZoom,
    onZoomChange,
    minZoom = MIN_ZOOM,
    maxZoom = MAX_ZOOM,
  } = options

  const isPinching = ref(false)
  let initialDistance = 0
  let initialZoom = 100

  function getDistance(touches: TouchList): number {
    if (touches.length < 2) return 0
    const touch0 = touches[0]
    const touch1 = touches[1]
    if (!touch0 || !touch1) return 0
    const dx = touch0.clientX - touch1.clientX
    const dy = touch0.clientY - touch1.clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  function handleTouchStart(e: TouchEvent) {
    if (e.touches.length === 2) {
      isPinching.value = true
      initialDistance = getDistance(e.touches)
      initialZoom = currentZoom.value
      e.preventDefault()
    }
  }

  function handleTouchMove(e: TouchEvent) {
    if (!isPinching.value || e.touches.length !== 2) return

    const currentDistance = getDistance(e.touches)
    if (initialDistance === 0) return

    const ratio = currentDistance / initialDistance
    const newZoom = clampZoom(initialZoom * ratio)
    const clamped = Math.max(minZoom, Math.min(maxZoom, newZoom))

    onZoomChange(clamped)
    e.preventDefault()
  }

  function handleTouchEnd(e: TouchEvent) {
    if (e.touches.length < 2) {
      isPinching.value = false
    }
  }

  onMounted(() => {
    const container = containerRef.value
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: false })
      container.addEventListener('touchmove', handleTouchMove, { passive: false })
      container.addEventListener('touchend', handleTouchEnd, { passive: true })
    }
  })

  onUnmounted(() => {
    const container = containerRef.value
    if (container) {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
    }
  })

  return { isPinching }
}
